import "@/App.css";
import { Suspense, lazy, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { AuthDeepLinkListener } from "@/components/AuthDeepLinkListener";
import AndroidBackHandler from "@/components/AndroidBackHandler";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import BottomNav from "@/components/BottomNav";
import FloatingBackPill from "@/components/FloatingBackPill";
import PermissionsPrompt from "@/components/PermissionsPrompt";
import TaxiRatingPrompt from "@/components/TaxiRatingPrompt";
import PullToRefresh from "@/components/PullToRefresh";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { Toaster } from "@/components/ui/sonner";
import { Splash } from "@/components/Splash";
import LanguageChooser from "@/components/LanguageChooser";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { UIProvider } from "@/lib/uiTexts";
import { UIDensityProvider } from "@/lib/uiDensity";
import OfflineBanner from "@/components/OfflineBanner";

// Eager: needed on first paint (home page)
import Home from "@/pages/Home";

// Lazy: everything else — dropped from initial bundle
const Category = lazy(() => import("@/pages/Category"));
const Search = lazy(() => import("@/pages/Search"));
const ShopDetail = lazy(() => import("@/pages/ShopDetail"));
const Auth = lazy(() => import("@/pages/Auth"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const ShopDashboard = lazy(() => import("@/pages/ShopDashboard"));
const PrinterStation = lazy(() => import("@/pages/PrinterStation"));
const Admin = lazy(() => import("@/pages/Admin"));
const AdminShopOperations = lazy(() => import("@/pages/admin/AdminShopOperations"));
const AdminFinance = lazy(() => import("@/pages/admin/AdminFinance"));
const AdminPushStatus = lazy(() => import("@/pages/admin/AdminPushStatus"));
const AdminCreateOrder = lazy(() => import("@/pages/admin/AdminCreateOrder"));
const AdminBroadcastNew = lazy(() => import("@/pages/admin/AdminBroadcastNew"));
const AdminSupportChats = lazy(() => import("@/pages/admin/AdminSupportChats"));
const SupportChat = lazy(() => import("@/pages/SupportChat"));
const AdminDeliveryHub = lazy(() => import("@/pages/admin/AdminDeliveryHub"));
const AdminManualDispatch = lazy(() => import("@/pages/admin/AdminManualDispatch"));
const HoodChainLobby = lazy(() => import("@/pages/HoodChain/HoodChainLobby"));
const HoodChainMatchmaking = lazy(() => import("@/pages/HoodChain/HoodChainMatchmaking"));
const HoodChainRoom = lazy(() => import("@/pages/HoodChain/HoodChainRoom"));
const HoodChainPlay = lazy(() => import("@/pages/HoodChain/HoodChainPlay"));
const AdminDriverBilling = lazy(() => import("@/pages/admin/AdminDriverBilling"));
const Jobs = lazy(() => import("@/pages/Jobs"));
const Promotions = lazy(() => import("@/pages/Promotions"));
const DownloadPage = lazy(() => import("@/pages/Download"));
const PaymentReturn = lazy(() => import("@/pages/PaymentReturn"));
const NewsList = lazy(() => import("@/pages/News").then((m) => ({ default: m.NewsList })));
const NewsDetail = lazy(() => import("@/pages/News").then((m) => ({ default: m.NewsDetail })));
const DeliveryDashboard = lazy(() => import("@/pages/DeliveryDashboard"));
const DriverAccount = lazy(() => import("@/pages/DriverAccount"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const Terms = lazy(() => import("@/pages/Terms"));
const DataDeletion = lazy(() => import("@/pages/DataDeletion"));
const AuthCallback = lazy(() => import("@/pages/AuthCallback"));
const MyFreeDeliveries = lazy(() => import("@/pages/MyFreeDeliveries"));
const MyAccount = lazy(() => import("@/pages/MyAccount"));
const MyBlocks = lazy(() => import("@/pages/MyBlocks"));
const Favorites = lazy(() => import("@/pages/Favorites"));
const TaxiDriverDashboard = lazy(() => import("@/pages/TaxiDriverDashboard"));
const Games = lazy(() => import("@/pages/Games"));

const DRIVER_ALLOWED = ["/delivery-dashboard", "/account", "/login", "/register", "/forgot-password", "/games"];

function DriverGate({ children }) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  // Taxi driver auto-redirect: if a taxi-driver session exists in local
  // storage AND the user is NOT authenticated as a regular user, bounce
  // them to /taxi-driver on cold start. Prevents them from being dropped
  // on the home screen (or any random category) after they close & reopen
  // the app, which previously made them feel "logged out".
  const taxiSess = typeof window !== "undefined" ? localStorage.getItem("kh_taxi_driver_session") : null;
  if (taxiSess && !user && (pathname === "/" || pathname === "")) {
    return <Navigate to="/taxi-driver" replace />;
  }
  if (user && user.role === "delivery_driver") {
    const ok = DRIVER_ALLOWED.some((p) => pathname === p || pathname.startsWith(p + "/"));
    if (!ok) return <Navigate to="/delivery-dashboard" replace />;
  }
  return children;
}

function PageLoader() {
  return (
    <div className="min-h-[50vh] flex items-center justify-center">
      <div className="size-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

// Wrapper that injects auth data into MyAccount page
function MyAccountWithAuth() {
  const { user, logout, refresh } = useAuth();
  return <MyAccount user={user} onLogout={logout} refreshUser={refresh} />;
}

function App() {
  // Bootstrap native push notifications (iOS/Android) as soon as the app
  // loads. On iOS this is what makes "Notifications" appear in Settings.
  // Safe no-op on the web AND on Android (Android push is guarded inside
  // native-push.js until Firebase is configured — see v1.0.10 fix).
  useEffect(() => {
    // Extra safety: wrap in try/catch AND setTimeout so any startup crash
    // in the push plugin can never freeze / kill the WebView on cold start.
    const t = setTimeout(() => {
      try {
        import("@/lib/native-push")
          .then(m => m.initNativePush().catch(() => {}))
          .catch(() => {});
      } catch { /* never let push init crash the app */ }
    }, 1200);  // 1.2s delay lets the WebView fully render before touching plugins

    // ---- PREFETCH heavy routes in the background after Home paints ----
    // This warms the JS chunks so tapping "Category" / "Shop" / "Dashboard"
    // opens instantly instead of showing PageLoader for 500-1500ms.
    // Uses requestIdleCallback so it never competes with critical paint.
    const prefetch = () => {
      const ric = window.requestIdleCallback || ((cb) => setTimeout(cb, 2500));
      ric(() => {
        // eslint-disable-next-line no-unused-expressions
        import("@/pages/Category");
        import("@/pages/ShopDetail");
        import("@/pages/Dashboard");
        import("@/pages/Search");
      }, { timeout: 5000 });
    };
    if (document.readyState === "complete") prefetch();
    else window.addEventListener("load", prefetch, { once: true });

    return () => clearTimeout(t);
  }, []);
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <LanguageProvider>
          <AuthProvider>
            <UIProvider>
            <UIDensityProvider>
            <BrowserRouter>
              <OfflineBanner />
              <AuthDeepLinkListener />
              <AndroidBackHandler />
              <div className="min-h-screen flex flex-col">
                <ConditionalHeader />
                <main className="flex-1">
                <PullToRefresh>
                <DriverGate>
                  <Suspense fallback={<PageLoader />}>
                    <ErrorBoundary>
                      <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/search" element={<Search />} />
                    <Route path="/category/:slug" element={<Category />} />
                    <Route path="/shop/:id" element={<ShopDetail />} />
                    <Route path="/print/:shopId" element={<PrinterStation />} />
                    <Route path="/login" element={<Auth mode="login" />} />
                    <Route path="/register" element={<Auth mode="register" />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/jobs" element={<Jobs />} />
                    <Route path="/news" element={<NewsList />} />
                    <Route path="/news/:id" element={<NewsDetail />} />
                    <Route path="/promotions" element={<Promotions />} />
                    <Route path="/download" element={<DownloadPage />} />
                    <Route path="/promotions/new" element={<ProtectedRoute><Promotions newMode /></ProtectedRoute>} />
                    <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                    <Route path="/account" element={<ProtectedRoute role="delivery_driver"><DriverAccount /></ProtectedRoute>} />
                    <Route path="/shop-dashboard" element={<ProtectedRoute role="shop_owner"><ShopDashboard /></ProtectedRoute>} />
                    <Route path="/delivery-dashboard" element={<ProtectedRoute role="delivery_driver"><DeliveryDashboard /></ProtectedRoute>} />
                    <Route path="/admin" element={<ProtectedRoute role="admin"><Admin /></ProtectedRoute>} />
                    <Route path="/admin/shops/:shopId/operations" element={<ProtectedRoute role="admin"><AdminShopOperations /></ProtectedRoute>} />
                    <Route path="/admin/finance" element={<ProtectedRoute role="admin"><AdminFinance /></ProtectedRoute>} />
                    <Route path="/admin/push" element={<ProtectedRoute role="admin"><AdminPushStatus /></ProtectedRoute>} />
                    <Route path="/admin/orders/new" element={<ProtectedRoute role="admin"><AdminCreateOrder /></ProtectedRoute>} />
                    <Route path="/admin/broadcast" element={<ProtectedRoute role="admin"><AdminBroadcastNew /></ProtectedRoute>} />
                    <Route path="/admin/support" element={<ProtectedRoute role="admin"><AdminSupportChats /></ProtectedRoute>} />
                    <Route path="/support-chat" element={<ProtectedRoute><SupportChat /></ProtectedRoute>} />
                    <Route path="/admin/dispatch" element={<ProtectedRoute role="admin"><AdminManualDispatch /></ProtectedRoute>} />
                    <Route path="/hood-chain" element={<ProtectedRoute><HoodChainLobby /></ProtectedRoute>} />
                    <Route path="/hood-chain/matchmaking" element={<ProtectedRoute><HoodChainMatchmaking /></ProtectedRoute>} />
                    <Route path="/hood-chain/room/:code" element={<ProtectedRoute><HoodChainRoom /></ProtectedRoute>} />
                    <Route path="/hood-chain/play/:code?" element={<ProtectedRoute><HoodChainPlay /></ProtectedRoute>} />
                    <Route path="/admin/delivery-hub" element={<ProtectedRoute role="admin"><AdminDeliveryHub /></ProtectedRoute>} />
                    <Route path="/admin/drivers/billing" element={<ProtectedRoute role="admin"><AdminDriverBilling /></ProtectedRoute>} />
                    <Route path="/payment/return" element={<PaymentReturn />} />
                    <Route path="/privacy" element={<Privacy />} />
                    <Route path="/terms" element={<Terms />} />
                    <Route path="/data-deletion" element={<DataDeletion />} />
                    <Route path="/auth-callback" element={<AuthCallback />} />
                    <Route path="/my-free-deliveries" element={<MyFreeDeliveries />} />
                    <Route path="/taxi-driver" element={<TaxiDriverDashboard />} />
                    <Route path="/games" element={<Games />} />
                    <Route path="/my-account" element={<ProtectedRoute><MyAccountWithAuth /></ProtectedRoute>} />
                    <Route path="/my-blocks" element={<ProtectedRoute><MyBlocks /></ProtectedRoute>} />
                    <Route path="/favorites" element={<Favorites />} />
                      </Routes>
                    </ErrorBoundary>
                </Suspense>
              </DriverGate>
                </PullToRefresh>
            </main>
            <DriverAwareFooter />
            <DriverAwareBottomNav />
          </div>
          <Toaster position="top-center" richColors closeButton />
          <Splash />
          <LanguageChooser />
          <OutsideGame><PermissionsPrompt /><TaxiRatingPrompt /></OutsideGame>
        </BrowserRouter>
        </UIDensityProvider>
        </UIProvider>
      </AuthProvider>
    </LanguageProvider>
    </ThemeProvider>
    </ErrorBoundary>
  );
}

const _hideChrome = (p) =>
  p === "/taxi-driver" ||
  p === "/games" ||
  p === "/hood-chain" ||
  p.startsWith("/hood-chain/");

function DriverAwareFooter() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  if (_hideChrome(pathname)) return null;
  if (user && user.role === "delivery_driver") return null;
  return <Footer />;
}

function DriverAwareBottomNav() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  if (_hideChrome(pathname)) return null;
  if (user && user.role === "delivery_driver") return null;
  return <BottomNav />;
}

function OutsideGame({ children }) {
  const { pathname } = useLocation();
  return pathname.startsWith("/hood-chain") ? null : children;
}

function ConditionalHeader() {
  const { pathname } = useLocation();
  if (_hideChrome(pathname)) return null;
  return <Header />;
}

export default App;
